import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import {
    Button,
    Heading1,
    Heading2,
    Stack,
    Subtitle1,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import {
    useDrugProgram,
    useMedicationConfigOverride,
} from '../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    type PatientInfoFragment,
    PrescriberOrderStatus,
} from '../../../graphql/generated/graphql';
import { useFlags } from '../../../hooks/use-flags';
import { DrugKeys } from '../../../util/config/constants';
import { getDrugConfigByMedication } from '../../../util/config/get-drug-config-by-medication';
import { getNdcDefinition } from '../../../util/config/get-ndc-definition';
import { getPricing } from '../../../util/config/get-pricing';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { getProviderName } from '../../../util/pharmacy/get-provider-name';
import { SendToPharmacyError } from '../../errors/SendToPharmacyError';
import { ProviderInfo } from '../../provider/ProviderInfo';
import { ConditionalComponent } from '../../utility/ConditionalComponent';

import { OnHoldModal } from './OnHoldModal';
import { OtherOptionsLink } from './OtherOptionsLink';
import PatientMedicationDetails from './PatientMedicationDetails';
import { PreviewCashDetails } from './PreviewCashDetails';
import { PreviewInsuranceDetails } from './PreviewInsuranceDetails';
import { PreviewKwikpenDetails } from './PreviewKwikpenDetails';
import { PreviewTempOrfoDetails } from './PreviewTempOrfoDetails';
import { ViewSwitchOptions } from './ViewSwitchOptions';

type PreviewDetailsProps = {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
    patient: PatientInfoFragment;
    sendPrescriberOrder: (providerId: string) => void;
    isSendLoading: boolean;
};

const PreviewDetails = ({
    prescriberOrder,
    patient,
    sendPrescriberOrder,
    isSendLoading,
}: PreviewDetailsProps) => {
    const { t } = useTranslation();
    const drugProgram = useDrugProgram();
    const configOverride = useMedicationConfigOverride();
    const [sendError, setSendError] = useState<boolean>(false);
    const { enableTempLiteFlow, enableUpdatedDrugSwitchLilly } = useFlags();

    const authorizedMedication = getFragment(
        prescriberOrder.authorizedMedication
    );

    const preferredProvidersConnection = getFragment(
        prescriberOrder.preferredProvidersConnection
    );
    const preferredProviders =
        preferredProvidersConnection?.edges.map((edge) =>
            getFragment(edge.node)
        ) ?? [];
    const showProvider = preferredProviders.length === 1;
    const provider = showProvider ? preferredProviders[0] : null;

    if (!authorizedMedication || preferredProviders.length === 0) {
        // oxlint-disable-next-line no-console
        console.log(
            'PreviewDetails: Missing authorized medication or preferred providers',
            authorizedMedication,
            preferredProviders
        );
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const isPrescriptionOnHold =
        prescriberOrder.status === PrescriberOrderStatus.OnHold;
    const [showOnHoldModal, { toggle: toggleOnHoldModal }] =
        // oxlint-disable-next-line react-hooks/rules-of-hooks -- tracked in docs/oxlint-violations.md
        useDisclosure(isPrescriptionOnHold);

    const drugConfig = getDrugConfigByMedication(
        drugProgram,
        authorizedMedication
    );
    const ndcDefinition = getNdcDefinition(drugProgram, authorizedMedication);
    const drugPricing = getPricing(configOverride, authorizedMedication);
    const providerOverrides =
        provider && configOverride.pharmacyOverrides[provider.id];

    if (!drugPricing || !drugConfig || !ndcDefinition) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const handleSendPrescriberOrder = async (providerId: string) => {
        setSendError(false);
        try {
            await sendPrescriberOrder(providerId);
        } catch {
            setSendError(true);
        }
    };

    return (
        <>
            <SendToPharmacyError show={sendError} />
            <Stack gap="lg" data-testid="pharmacy-preview-page-root">
                <PatientMedicationDetails
                    patientName={patient.fullName}
                    prescriberOrder={prescriberOrder}
                />

                <ConditionalComponent condition={!isPrescriptionOnHold}>
                    {enableUpdatedDrugSwitchLilly ? (
                        <ViewSwitchOptions
                            ndcDefinition={ndcDefinition}
                            order={prescriberOrder}
                            position="details"
                        />
                    ) : (
                        <OtherOptionsLink
                            drugConfig={drugConfig}
                            order={prescriberOrder}
                        />
                    )}
                </ConditionalComponent>

                <Stack gap="md">
                    <ConditionalComponent
                        condition={
                            !!(
                                showProvider &&
                                provider &&
                                drugPricing?.type === 'selfPay'
                            )
                        }
                    >
                        <Heading1>{t('previewDetails.title')}</Heading1>
                        <Subtitle1>
                            {getProviderName(provider, providerOverrides)}
                        </Subtitle1>
                    </ConditionalComponent>

                    <ConditionalComponent
                        condition={
                            drugPricing?.type === 'selfPay' &&
                            drugConfig.drugKey === DrugKeys.ZepboundVial
                        }
                    >
                        <PreviewCashDetails
                            pricingInfo={drugPricing}
                            medication={authorizedMedication}
                        />
                    </ConditionalComponent>

                    <ConditionalComponent
                        condition={
                            enableTempLiteFlow &&
                            drugConfig.drugKey === DrugKeys.Orfo
                        }
                    >
                        <PreviewTempOrfoDetails
                            pricingInfo={drugPricing}
                            medication={authorizedMedication}
                        />
                    </ConditionalComponent>

                    <ConditionalComponent
                        condition={
                            drugPricing?.type === 'selfPay' &&
                            drugConfig.drugKey === DrugKeys.ZepboundKwikPen
                        }
                    >
                        <PreviewKwikpenDetails
                            pricingInfo={drugPricing}
                            medication={authorizedMedication}
                        />
                    </ConditionalComponent>

                    <ConditionalComponent
                        condition={drugPricing?.type === 'insurance'}
                    >
                        <PreviewInsuranceDetails
                            ndcDefinition={ndcDefinition}
                            prescriberOrder={prescriberOrder}
                            pricingInfo={drugPricing}
                        />
                    </ConditionalComponent>

                    <ConditionalComponent
                        condition={showProvider && !!provider}
                    >
                        <Stack gap="sm">
                            <Heading2>
                                {t('previewDetails.pharmacy.header')}
                            </Heading2>
                            <ProviderInfo provider={provider!} />
                        </Stack>
                    </ConditionalComponent>

                    <ConditionalComponent
                        condition={
                            preferredProviders.length > 1 &&
                            !isPrescriptionOnHold
                        }
                    >
                        <Button
                            link
                            to={getAbsoluteRoute(
                                'order.prescriberOrderId.root',
                                {
                                    prescriberOrderId:
                                        prescriberOrder.drxId ?? '',
                                }
                            )}
                            size="lg"
                        >
                            {t('previewDetails.selectPharmacy')}
                        </Button>
                    </ConditionalComponent>

                    <ConditionalComponent
                        condition={
                            preferredProviders.length === 1 &&
                            !isPrescriptionOnHold
                        }
                    >
                        <Button
                            size="lg"
                            onClick={() =>
                                handleSendPrescriberOrder(provider!.id)
                            }
                            loading={isSendLoading}
                        >
                            {t('previewDetails.sendToPharmacy')}
                        </Button>
                    </ConditionalComponent>

                    <ConditionalComponent condition={isPrescriptionOnHold}>
                        <Button size="lg" onClick={toggleOnHoldModal}>
                            {t('previewDetails.unlockPrescription')}
                        </Button>
                    </ConditionalComponent>
                </Stack>
                <OnHoldModal
                    show={showOnHoldModal}
                    order={prescriberOrder}
                    patientId={patient.id}
                    toggle={toggleOnHoldModal}
                />
            </Stack>
        </>
    );
};

export default PreviewDetails;
